import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../config/api";
import Loading from "../Components/Loading";

const MAX_PDF_SIZE = 10 * 1024 * 1024;
const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

const TABS = [
  { id: "terms", label: "Terms" },
  { id: "privacy", label: " Leave Policy" },
];

const TermsAndConditions = () => {
  const role = localStorage.getItem("role");
  const currentUserId = localStorage.getItem("id");
  const designation = (localStorage.getItem("designationName") || "")
    .replace(/\u00A0/g, " ")
    .trim()
    .toLowerCase();
  const adminDesignation = (process.env.REACT_APP_ADMIN_DESIGNATION || "")
    .trim()
    .toLowerCase();
  const isAdminLike =
    role === "admin" ||
    (!!adminDesignation && designation === adminDesignation);

  const [activeTab, setActiveTab] = useState("terms");
  const [documents, setDocuments] = useState({ terms: null, privacy: null });
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("terms");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchDocument = async (policyType) => {
    if (!API_URL) {
      toast.error("API URL is not configured.");
      setLoadingPolicy(false);
      return;
    }
    setLoadingPolicy(true);
    try {
      const res = await axios.post(
        API_URL,
        { action: "get-policy-document", policyType },
        JSON_HEADERS,
      );
      if (res.data?.success) {
        setDocuments((prev) => ({
          ...prev,
          [policyType]: res.data.document || null,
        }));
      } else {
        toast.error(res.data?.message || "Failed to load document");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load document");
    } finally {
      setLoadingPolicy(false);
    }
  };

  useEffect(() => {
    fetchDocument(activeTab);
  }, [activeTab]);

  const openModal = () => {
    setModalType(activeTab);
    setFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (uploading) return;
    setShowModal(false);
    setFile(null);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) {
      setFile(null);
      return;
    }
    const isPdf =
      selected.type === "application/pdf" ||
      selected.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Only PDF files are allowed");
      e.target.value = "";
      setFile(null);
      return;
    }
    if (selected.size > MAX_PDF_SIZE) {
      toast.error("File is too large. Maximum size is 10 MB");
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a PDF file");
      return;
    }

    setUploading(true);
    try {
      const urlRes = await axios.post(
        API_URL,
        {
          action: "get-policy-upload-url",
          currentUserId,
          policyType: modalType,
          fileName: file.name,
        },
        JSON_HEADERS,
      );

      if (!urlRes.data?.success) {
        throw new Error(urlRes.data?.message || "Failed to get upload URL");
      }
      const { uploadUrl, s3Key } = urlRes.data.data;

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error("Failed to upload file to storage");
      }

      const saveRes = await axios.post(
        API_URL,
        {
          action: "save-policy-document",
          currentUserId,
          policyType: modalType,
          s3Key,
          fileName: file.name,
        },
        JSON_HEADERS,
      );

      if (!saveRes.data?.success) {
        throw new Error(saveRes.data?.message || "Failed to save document");
      }

      toast.success(saveRes.data.message || "Document uploaded successfully");
      setDocuments((prev) => ({ ...prev, [modalType]: saveRes.data.document }));
      setActiveTab(modalType);
      setShowModal(false);
      setFile(null);
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Upload failed",
      );
    } finally {
      setUploading(false);
    }
  };

  const currentDoc = documents[activeTab];

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">Terms and Conditions</h1>
              <p className="employee-description">
                View the company Terms and Conditions and Leave Policy.
              </p>
            </div>
            <div className="col-md-4 text-right">
              {isAdminLike && (
                <button
                  type="button"
                  className="btn-add btn-enabled"
                  onClick={openModal}
                >
                  Add
                </button>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12 tabs-container">
              <ul className="nav nav-tabs employee-tabs-list" role="tablist">
                {TABS.map((tab) => (
                  <li className="nav-item" key={tab.id}>
                    <div
                      className={`nav-link employee-tab-link ${activeTab === tab.id ? "active" : ""}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="card employee-content-card">
            <div className="card-body">
              {loadingPolicy ? (
                <Loading size={80} />
              ) : currentDoc?.viewUrl ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>
                      <i className="fas fa-file-pdf mr-2"></i>
                      {currentDoc.fileName}
                    </span>
                    <a
                      href={currentDoc.viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in new tab
                    </a>
                  </div>
                  <iframe
                    title={currentDoc.title}
                    src={currentDoc.viewUrl}
                    style={{
                      width: "100%",
                      height: "75vh",
                      border: "1px solid #e5e7eb",
                    }}
                  />
                </>
              ) : (
                <div className="empty-state-container">
                  <i className="fas fa-file-pdf empty-state-icon"></i>
                  <p>
                    {TABS.find((t) => t.id === activeTab)?.label} document has
                    not been uploaded yet.
                    {isAdminLike && ' Click "Add" to upload a PDF.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {isAdminLike && showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content"
            style={{
              width: "600px",
              maxWidth: "100%",
              height: "auto",
              minHeight: "0",
              paddingBottom: "0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5 className="modal-title">Upload Document</h5>
              <button
                type="button"
                className="modal-close"
                onClick={closeModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpload}>
                <div className="form-group">
                  <label htmlFor="policyType" className="form-label">
                    Document Type
                  </label>
                  <select
                    id="policyType"
                    className="form-control"
                    value={modalType}
                    onChange={(e) => setModalType(e.target.value)}
                    disabled={uploading}
                  >
                    <option value="terms">Terms and Conditions</option>
                    <option value="privacy">Leave Policy</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="policyFile" className="form-label">
                    PDF File<span className="required-star">*</span>
                  </label>
                  <input
                    type="file"
                    id="policyFile"
                    className="form-control file-input-padding"
                    accept="application/pdf,.pdf"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <small className="form-text text-muted">
                    PDF only, max 10 MB. Uploading again replaces the existing
                    document.
                  </small>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeModal}
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={uploading || !file}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsAndConditions;